import { apiClient } from './api'
import type { Project } from '@/types/api'

interface ProjectResponse {
  success: boolean
  project?: Project
  message?: string
}

interface ProjectsResponse {
  success: boolean
  projects?: Project[]
  message?: string
}

class ProjectService {
  async getUserProjects(): Promise<ProjectsResponse> {
    try {
      const response = await apiClient.get('/projects')
      
      return {
        success: true,
        projects: response.data.data || response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch projects'
      }
    }
  }

  async getProject(projectId: string): Promise<ProjectResponse> {
    try {
      const response = await apiClient.get(`/projects/${projectId}`)
      
      return {
        success: true,
        project: response.data.data || response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project'
      }
    }
  }

  async createProject(projectData: {
    title: string
    description?: string
    type: string // Backend will validate this against available types
    parent_id?: string
  }): Promise<ProjectResponse> {
    try {
      const response = await apiClient.post('/projects', projectData)
      
      return {
        success: true,
        project: response.data.data || response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to create project'
      }
    }
  }

  async updateProject(projectId: string, updates: Partial<Project>): Promise<ProjectResponse> {
    try {
      const response = await apiClient.put(`/projects/${projectId}`, updates)
      
      return {
        success: true,
        project: response.data.data || response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to update project'
      }
    }
  }

  async deleteProject(projectId: string): Promise<{ success: boolean; message?: string }> {
    try {
      await apiClient.delete(`/projects/${projectId}`)
      
      return {
        success: true
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to delete project'
      }
    }
  }

  async getProjectHierarchy(projectId?: string): Promise<ProjectsResponse> {
    try {
      const url = projectId ? `/projects/${projectId}/hierarchy` : '/projects/hierarchy'
      const response = await apiClient.get(url)
      
      return {
        success: true,
        projects: response.data.data || response.data
      }
    } catch (error: any) {
      return {
        success: false,
        message: error.response?.data?.message || 'Failed to fetch project hierarchy'
      }
    }
  }
}

export const projectService = new ProjectService()